import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { BaseDatatableStore, IBaseDatatableState } from '../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { IEditKanbanView, IKanbanView, TAddKanbanView } from './kanban-views.interface';

export interface IKanbanViewComponentState extends IBaseDatatableState<IKanbanView> {
  bulkCrudLoading: ELoadStatus;
  bulkOperationFailedData: IBaseCrudResponse[];
  data: IKanbanView[];
  dataLoading: ELoadStatus;
  dataTotal: number;
  singleCrudLoading: ELoadStatus;
}

@Injectable()
export class KanbanViewsStore extends BaseDatatableStore<IKanbanView, IKanbanViewComponentState> {
  readonly createOne = this.effect((trigger$: Observable<TAddKanbanView>) =>
    trigger$.pipe(
      switchMap((item: TAddKanbanView) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IEditKanbanView>) =>
    trigger$.pipe(
      switchMap((item: IEditKanbanView) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateItem(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteItem(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getItems(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IKanbanView>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private createItem(item: TAddKanbanView): Observable<IBaseResponse<IKanbanView>> {
    return this.http.post<IBaseResponse<IKanbanView>>('kanban-views', item);
  }

  private updateItem(item: IEditKanbanView): Observable<IBaseResponse<IKanbanView>> {
    return this.http.patch<IBaseResponse<IKanbanView>>(`kanban-views/${item.id}`, item.dto);
  }

  private deleteItem(id: number): Observable<IBaseResponse<IKanbanView>> {
    return this.http.delete<IBaseResponse<IKanbanView>>(`kanban-views/${id}`);
  }

  private getItems(params: HttpParams): Observable<IGetManyResponse<IKanbanView>> {
    return this.http.get<IGetManyResponse<IKanbanView>>('kanban-views', { params });
  }
}
